import {
  Activity,
  BadgeCheck,
  Code2,
  ChevronDown,
  Files,
  GitPullRequest,
  House,
  ListTodo,
  MessagesSquare,
  NotebookTabs,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { SelectField } from '@/shared/ui/select';
import { Tooltip } from '@/shared/ui/tooltip';

import { searchField } from '../field-overview';
import { FIELD_SECTIONS } from '../field-view';

import type { FieldDestination } from '../field-overview';
import type { FieldSection } from '../field-view';
import type { FieldSnapshot } from '@mio/shared';

const icons = {
  progress: Activity,
  today: House,
  engagement: NotebookTabs,
  build: Code2,
  inbox: MessagesSquare,
  board: ListTodo,
  reviews: GitPullRequest,
  documents: Files,
  operations: Activity,
  assessment: BadgeCheck,
};

export function FieldNavigation({
  section,
  onChange,
  unreadCount,
  snapshot,
  onNavigate,
  collapsed = false,
  onExpand,
}: {
  collapsed?: boolean;
  onExpand?: () => void;
  section: FieldSection;
  onChange: (section: FieldSection) => void;
  unreadCount: number;
  snapshot?: FieldSnapshot;
  onNavigate?: (destination: FieldDestination) => void;
}) {
  const [query, setQuery] = useState('');
  const [more, setMore] = useState(false);
  const navigation = useRef<HTMLElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const unreadId = useId();
  const focusSearch = useRef(false);
  useEffect(() => {
    if (!collapsed && focusSearch.current) {
      input.current?.focus();
      focusSearch.current = false;
    }
  }, [collapsed]);
  const searching = Boolean(query.trim());
  useEffect(() => {
    if (!searching) return;
    const dismiss = (event: PointerEvent) => {
      if (!navigation.current?.contains(event.target as Node)) setQuery('');
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [searching]);
  const results = snapshot ? searchField(snapshot, query) : [];
  const primary = snapshot?.discovery
    ? [
        { id: 'engagement' as const, label: 'Brief' },
        { id: 'build' as const, label: 'Build' },
        { id: 'inbox' as const, label: 'Conversations' },
        { id: 'reviews' as const, label: 'Reviews' },
      ]
    : FIELD_SECTIONS.filter(
        (item) => item.id !== 'engagement' && item.id !== 'build' && item.id !== 'progress',
      );
  const secondary = snapshot?.discovery
    ? FIELD_SECTIONS.filter(
        (item) =>
          item.id !== 'progress' && !primary.some((primaryItem) => primaryItem.id === item.id),
      )
    : [];
  const expanded = more;
  const previousSection = useRef(section);
  useEffect(() => {
    if (
      previousSection.current !== section &&
      snapshot?.discovery &&
      !['progress', 'engagement', 'build', 'inbox', 'reviews'].includes(section)
    )
      setMore(true);
    previousSection.current = section;
  }, [section, snapshot?.discovery]);
  const sections = [
    ...(snapshot?.discovery ? [{ id: 'progress' as const, label: 'Progress' }] : []),
    ...primary,
    ...secondary,
  ];
  const visibleSections = [
    ...primary,
    ...(secondary.length ? [{ id: 'tools' as const, label: 'Project tools' }] : []),
    ...(expanded ? secondary : []),
  ];
  return (
    <nav
      ref={navigation}
      aria-label="Workbench workspace"
      className="field-navigation"
      data-collapsed={collapsed || undefined}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && searching) {
          setQuery('');
          input.current?.focus();
        }
      }}
    >
      {snapshot ? (
        <div className="field-navigation-intro">
          <div className="field-navigation-identity">
            <p>{snapshot.discovery ? 'Delivery engagement' : 'Engagement'}</p>
            <strong>
              Day {snapshot.engagement.day} {!snapshot.discovery ? <span>of 10</span> : null}
            </strong>
          </div>
          {!snapshot.discovery ? (
            <div className="field-day-strip" aria-hidden="true">
              {Array.from({ length: 10 }, (_, index) => (
                <span
                  key={index}
                  data-state={
                    index + 1 === snapshot.engagement.day
                      ? 'current'
                      : index < snapshot.engagement.day
                        ? 'past'
                        : 'future'
                  }
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="field-navigation-mobile">
        <SelectField
          value={section}
          onValueChange={(value) => {
            onChange(value as FieldSection);
            setQuery('');
          }}
          aria-label="Workbench section"
          options={sections.map((item) => ({
            value: item.id,
            label:
              item.id === 'inbox' && unreadCount > 0
                ? `${item.label} (${unreadCount})`
                : item.label,
          }))}
        />
      </div>
      {snapshot && collapsed ? (
        <Tooltip label="Search Workbench">
          <button
            type="button"
            className="field-rail-search"
            data-field-tour="field-search"
            aria-label="Search Workbench"
            onClick={() => {
              focusSearch.current = true;
              onExpand?.();
            }}
          >
            <Search size={17} aria-hidden="true" />
          </button>
        </Tooltip>
      ) : snapshot ? (
        <div className="field-search" data-field-tour="field-search">
          <Search size={15} aria-hidden="true" />
          <input
            ref={input}
            type="search"
            aria-label="Search Workbench"
            placeholder="Find work or people"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          {query ? (
            <button
              aria-label="Clear Workbench search"
              onClick={() => {
                setQuery('');
                input.current?.focus();
              }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
      {query.trim() && !collapsed ? (
        <div className="field-search-results" aria-label="Workbench search results">
          <p role="status">
            {results.length ? `${results.length} results` : 'No matching work or people'}
          </p>
          {results.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                onNavigate?.(item);
                setQuery('');
              }}
            >
              <strong>{item.title}</strong>
              <span>{item.detail}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="field-navigation-items">
          {visibleSections.map((item, index) => {
            if (item.id === 'tools')
              return (
                <button
                  key="tools"
                  type="button"
                  aria-label="Project tools"
                  aria-expanded={expanded}
                  onClick={() => {
                    if (collapsed) onExpand?.();
                    setMore(!expanded);
                  }}
                  className="field-nav-group-start"
                >
                  <ChevronDown
                    size={18}
                    aria-hidden="true"
                    className={expanded ? 'rotate-180' : undefined}
                  />
                  <span className="field-nav-label">Project tools</span>
                </button>
              );
            const Icon = icons[item.id];
            return (
              <Tooltip key={item.id} label={item.label} disabled={!collapsed}>
                <button
                  type="button"
                  aria-label={item.label}
                  aria-describedby={item.id === 'inbox' && unreadCount > 0 ? unreadId : undefined}
                  aria-current={section === item.id ? 'page' : undefined}
                  onClick={() => {
                    onChange(item.id);
                    setQuery('');
                  }}
                  className={index === primary.length ? 'field-nav-group-start' : undefined}
                >
                  <Icon size={18} strokeWidth={1.7} aria-hidden="true" />
                  <span className="field-nav-label">{item.label}</span>
                  {item.id === 'inbox' && unreadCount > 0 ? (
                    <span
                      id={unreadId}
                      className="field-unread-count"
                      aria-label={`${unreadCount} unread`}
                    >
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  ) : null}
                </button>
              </Tooltip>
            );
          })}
        </div>
      )}
      <p className="field-navigation-note">
        {snapshot?.discovery ? 'Your work. Your delivery plan.' : 'You decide when the day ends.'}
        <span>Changes save when you submit them.</span>
      </p>
    </nav>
  );
}
